import React, { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "@/lib/utils";
import { toBcp47 } from "@/utils/date";

function capitalizeFirst(value: string): string {
  return value.charAt(0).toLocaleUpperCase() + value.slice(1);
}

interface MonthYearPickerProps {
  /** "YYYY-MM" or "" */
  value: string;
  onChange: (value: string) => void;
  /** "YYYY-MM" inclusive minimum */
  min?: string;
  /** "YYYY-MM" inclusive maximum */
  max?: string;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
}

function parseYM(ym: string): { year: number; month: number } | null {
  if (!ym) return null;
  const [y, m] = ym.split("-").map(Number);
  return { year: y, month: m };
}

function toYM(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  value,
  onChange,
  min,
  max,
  placeholder,
  className,
  triggerClassName,
}) => {
  const { t, i18n } = useTranslation();
  const locale = toBcp47(i18n.language);

  const monthsFull = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        capitalizeFirst(
          new Intl.DateTimeFormat(locale, { month: "long" }).format(
            new Date(2000, i, 1),
          ),
        ),
      ),
    [locale],
  );
  const monthsShort = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        capitalizeFirst(
          new Intl.DateTimeFormat(locale, { month: "short" }).format(
            new Date(2000, i, 1),
          ),
        ),
      ),
    [locale],
  );
  const resolvedPlaceholder = placeholder ?? t("common.select_month_year");

  const parsed = parseYM(value);
  const minParsed = parseYM(min ?? "");
  const maxParsed = parseYM(max ?? "");

  const currentYear = new Date().getFullYear();
  const [displayYear, setDisplayYear] = useState<number>(
    parsed?.year ?? currentYear
  );
  const [open, setOpen] = useState(false);

  // Sync displayYear if value changes externally (e.g. parent resets to "")
  useEffect(() => {
    setDisplayYear(parsed?.year ?? currentYear);
  }, [value]);

  const isMonthDisabled = (month: number): boolean => {
    const ym = toYM(displayYear, month);
    if (minParsed && ym < toYM(minParsed.year, minParsed.month)) return true;
    if (maxParsed && ym > toYM(maxParsed.year, maxParsed.month)) return true;
    return false;
  };

  const canGoPrev = !minParsed || displayYear > minParsed.year;
  const canGoNext = !maxParsed || displayYear < maxParsed.year;

  const displayLabel = parsed
    ? `${monthsFull[parsed.month - 1]} ${parsed.year}`
    : "";

  const handleSelect = (month: number) => {
    onChange(toYM(displayYear, month));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "w-full flex items-center justify-between bg-white border border-[#efefef] rounded-md font-inter text-left",
            triggerClassName,
            className
          )}
        >
          <span className={cn(!displayLabel && "text-[#9EA3AB]")}>
            {displayLabel || resolvedPlaceholder}
          </span>
          <ChevronDown className="w-4 h-4 text-[#9EA3AB] shrink-0" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-64 p-3 bg-white border border-[#efefef] rounded-lg shadow-lg"
        align="start"
        sideOffset={6}
      >
        {/* Year navigation */}
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => setDisplayYear((y) => y - 1)}
            disabled={!canGoPrev}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-zinc-700" />
          </button>
          <span className="text-sm font-semibold font-inter text-zinc-900">
            {displayYear}
          </span>
          <button
            type="button"
            onClick={() => setDisplayYear((y) => y + 1)}
            disabled={!canGoNext}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-zinc-700" />
          </button>
        </div>

        {/* Month grid */}
        <div className="grid grid-cols-3 gap-1">
          {monthsShort.map((label, i) => {
            const month = i + 1;
            const disabled = isMonthDisabled(month);
            const selected =
              parsed?.year === displayYear && parsed?.month === month;

            return (
              <button
                key={label}
                type="button"
                disabled={disabled}
                onClick={() => handleSelect(month)}
                style={
                  selected
                    ? { backgroundColor: "var(--dp-brand-button)", color: "white" }
                    : undefined
                }
                className={cn(
                  "py-1.5 rounded-md text-sm font-inter font-medium transition-colors",
                  selected
                    ? "text-white"
                    : "text-zinc-700 hover:bg-gray-100",
                  disabled && "opacity-30 cursor-not-allowed hover:bg-transparent"
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MonthYearPicker;
